import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { products } from '@/utils/api';
import { toast } from 'sonner';
import { ShoppingCart, User, Package, Mail, Phone, MessageCircle, MapPin } from 'lucide-react';
import RazorpayCheckout from '@/components/RazorpayCheckout';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      const response = await products.getById(id);
      setProduct(response.data);
      // Load artist info
      if (response.data?.artistId) {
        loadArtist(response.data.artistId);
      }
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const loadArtist = async (artistId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/artists/${artistId}`);
      if (response.ok) {
        const data = await response.json();
        setArtist(data);
      }
    } catch (error) {
      console.error('Error loading artist:', error);
    }
  };

  const handleContactDulciaArt = () => {
    // Scroll to contact section
    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      `Hi ARTCARDO! I'm interested in "${product.title}" by ${product.artistName} (₹${product.price.toLocaleString()}). Please connect me with the artist.`
    );
    window.open(`https://wa.me/919019028615?text=${message}`, '_blank');
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(`Purchase Inquiry: ${product.title}`);
    const body = encodeURIComponent(
      `Hi ARTCARDO,\n\nI'm interested in the following artwork:\n\nTitle: ${product.title}\nArtist: ${product.artistName}\nPrice: ₹${product.price.toLocaleString()}\n\nPlease connect me with the artist to discuss:\n- Availability\n- Shipping details\n- Payment options\n\nThank you!`
    );
    window.location.href = `mailto:dulciaart@gmail.com?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <div data-testid="product-detail-page">
        <Navbar />
        <div className="section" style={{paddingTop: "180px"}}>
          <div className="text-center py-12">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div data-testid="product-detail-page">
        <Navbar />
        <div className="section" style={{paddingTop: "180px"}}>
          <div className="text-center py-12">
            <p className="text-lg text-gray-500">Product not found</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const gstAmount = product.price * (product.gstRate / 100);
  const totalPrice = product.price + gstAmount;

  return (
    <div data-testid="product-detail-page">
      <SEO 
        title={product.title}
        description={`${product.title} by ${product.artistName}. ${product.description?.substring(0, 150)}...`}
        keywords={`${product.category}, ${product.artistName}, buy art, ${product.title}`}
        image={products.getImage(product.images[0])}
        url={`/products/${product.id}`}
        type="product"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": product.title,
          "image": products.getImage(product.images[0]),
          "description": product.description,
          "brand": {
            "@type": "Brand",
            "name": product.artistName
          },
          "offers": {
            "@type": "Offer",
            "url": `https://www.artcardo.com/products/${product.id}`,
            "priceCurrency": "INR",
            "price": product.price,
            "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            "seller": {
              "@type": "Organization",
              "name": "ARTCARDO"
            }
          },
          "category": product.category,
          "aggregateRating": product.likes > 0 ? {
            "@type": "AggregateRating",
            "ratingValue": "5",
            "ratingCount": product.likes
          } : undefined
        }}
      />
      <Navbar />
      
      <div style={{paddingTop: "180px"}}>
        <section className="section">
          <div className="grid md:grid-cols-2 gap-12">
            <div data-testid="product-images">
              <div className="mb-4">
                <img
                  src={products.getImage(product.images[currentImage])}
                  alt={product.title}
                  className="w-full h-[500px] object-cover rounded-lg shadow-lg"
                  data-testid="main-product-image"
                />
              </div>
              {product.images.length > 1 && (
                <div className="flex gap-3" data-testid="image-thumbnails">
                  {product.images.map((img, idx) => (
                    <img
                      key={idx}
                      src={products.getImage(img)}
                      alt={`${product.title} ${idx + 1}`}
                      className={`w-20 h-20 object-cover rounded cursor-pointer ${
                        currentImage === idx ? 'border-2 border-[#ef4444]' : 'opacity-60'
                      }`}
                      onClick={() => setCurrentImage(idx)}
                      data-testid={`thumbnail-${idx}`}
                    />
                  ))}
                </div>
              )}
            </div>

            <div data-testid="product-info">
              <div className="mb-4">
                <span className="badge badge-approved" data-testid="product-category">{product.category}</span>
              </div>
              <h1 className="text-4xl font-bold mb-4" data-testid="product-title">{product.title}</h1>
              
              <Link to={`/artists/${product.artistId}`} className="flex items-center gap-2 mb-6 text-[#ef4444] hover:underline" data-testid="artist-link">
                <User className="w-4 h-4" />
                <span>by {product.artistName}</span>
              </Link>

              <div className="bg-[#f5f5f5] p-6 rounded-lg mb-6" data-testid="pricing-info">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Base Price:</span>
                  <span className="font-semibold">₹{product.price.toLocaleString()}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">GST ({product.gstRate}%):</span>
                  <span className="font-semibold">₹{gstAmount.toLocaleString()}</span>
                </div>
                <div className="border-t border-[#ef4444] pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="text-lg font-bold">Total Price:</span>
                    <span className="text-2xl font-bold text-[#ef4444]">₹{totalPrice.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4" data-testid="stock-info">
                  <Package className="w-5 h-5 text-[#ef4444]" />
                  <span className="text-gray-600">
                    {product.stock > 0 ? (
                      <span className="text-green-600">In Stock ({product.stock} available)</span>
                    ) : (
                      <span className="text-red-600">Out of Stock</span>
                    )}
                  </span>
                </div>
              </div>

              {/* Razorpay Checkout Button */}
              {product.stock > 0 ? (
                <div className="mb-4">
                  <RazorpayCheckout 
                    product={product} 
                    onSuccess={() => {
                      toast.success('Thank you for your purchase!');
                      navigate('/buyer-dashboard');
                    }}
                  />
                </div>
              ) : (
                <Button
                  disabled
                  className="w-full py-6 text-lg mb-3"
                  style={{background: '#gray'}}
                >
                  Out of Stock
                </Button>
              )}

              {/* Platform Info */}
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-sm text-blue-800 font-bold mb-2">
                  Easy Purchase - Secure Payment
                </h4>
                <p className="text-xs text-blue-700">
                  • Multiple payment options: Cards, UPI, Net Banking, Wallets<br/>
                  • 100% secure & encrypted transactions via Razorpay<br/>
                  • Instant payment confirmation<br/>
                  • Direct delivery from artist to you<br/>
                  • Order tracking in your dashboard
                </p>
              </div>

              {/* Customer Support */}
              <div id="contact-section" className="mt-6">
                <p className="text-sm text-gray-600 font-medium mb-3">Need assistance? Contact ARTCARDO Support:</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={handleWhatsApp}
                    variant="outline"
                    className="w-full"
                    data-testid="whatsapp-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                  <Button
                    onClick={handleEmail}
                    variant="outline"
                    className="w-full"
                    data-testid="email-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Button>
                </div>
              </div>

              {/* DULCIA.ART Contact Info */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold mb-3 text-gray-800">Contact ARTCARDO</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4" />
                    <a href="mailto:dulciaart@gmail.com" className="hover:text-[#ef4444]">
                      dulciaart@gmail.com
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone className="w-4 h-4" />
                    <a href="tel:+919019028615" className="hover:text-[#ef4444]">
                      +91 9019028615
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span>DULCIA.ART, Bangalore, India</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  We facilitate connections between artists and buyers at NO COST. Payment and delivery are directly between you and the artist.
                </p>
              </div>

              {/* Artist Info */}
              {artist && (
                <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-semibold mb-3 text-green-900">About the Artist</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-green-800">
                      <User className="w-4 h-4" />
                      <span className="font-medium">{product.artistName}</span>
                    </div>
                    {artist.bio && (
                      <p className="text-green-700 text-xs mt-2">{artist.bio}</p>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-3">Description</h3>
                <p className="text-gray-600 leading-relaxed" data-testid="product-description">
                  {product.description}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetail;